import classNames from "classnames";
import React, { useEffect, useRef, useState } from "react";

const ExpansionPanel = ({
  children,
  headerClass,
  isHeader = true,
  isOpen,
  emptyWhenClosed = false,
  className,
}: {
  children?: React.ReactNode;
  headerClass?: string;
  isHeader?: boolean;
  isOpen?: boolean;
  emptyWhenClosed?: boolean;
  className?: string;
}) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [contentHeight, setContentHeight] = useState<number>(0);
  const contentRef = useRef<HTMLDivElement>(null);

  // Enforce maximum of two children
  const childrenArray = React.Children.toArray(children);
  if (childrenArray.length > 2) {
    throw new Error(
      "ExpansionPanel accepts a maximum of two children: one for header and one for content."
    );
  }

  const togglePanel = () => {
    const content = contentRef.current;
    if (content) {
      if (isExpanded) {
        setContentHeight(content.scrollHeight);
        // Expanding: position back in place, then animate height
        requestAnimationFrame(() => {
          content.style.maxHeight = content.scrollHeight + "px";
        });
      } else {
        // Collapsing: animate to 0 height first
        content.style.maxHeight = content.scrollHeight + "px";
        requestAnimationFrame(() => {
          content.style.maxHeight = "0";
        });
      }
    }
  };

  useEffect(() => {
    if (isOpen !== undefined) setIsExpanded(isOpen);
  }, [isOpen]);

  // Sync local state with prop changes
  useEffect(() => {
    togglePanel();
  }, [isExpanded]);

  // Assign header and content from children
  const header: React.ReactNode = childrenArray[0];
  const content: React.ReactNode = (isHeader
    ? childrenArray[1]
    : childrenArray[0]) || (
    <div className={classNames({"p-3": !emptyWhenClosed})} style={{ height: `${contentHeight}px` }}></div>
  );

  return (
    <div className={classNames("expansion-panel", className)}>
      {isHeader && (
        <div
          className={classNames("expansion-header", headerClass)}
          onClick={
            isOpen === undefined ? () => setIsExpanded(!isExpanded) : undefined
          }
        >
          {header}
        </div>
      )}
      <div
        className={`expansion-content ${isExpanded ? "expanded" : ""}`}
        ref={contentRef}
        style={{
          maxHeight: "0",
          transition: "max-height 0.3s ease",
        }}
      >
        <div
          style={{
            pointerEvents: isExpanded ? "auto" : "none",
          }}
        >
          {content}
        </div>
      </div>
    </div>
  );
};

export default ExpansionPanel;
